import { CheckboxField, Divider, FileUpload, UploadArea, FilesList, Flex, Heading, Link, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import React, { type ReactNode } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { getStageCompletionStatusQueryOptions } from "shared-ui";
import { handleContactTeam, uploadManagerHookFactory } from "shared-utils";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { DOCUMENT_TYPE_UPLOADS_API, VALIDATED_DOCUMENT_TYPE_UPLOADS_API } from "../../../components/constants";
import { useDocumentQuery } from "../../../queries/document-uploads/queries";
import { useValidatedDocumentQuery } from "../../../queries/document-uploads/validated-documents-queries";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";

import { MESSAGES } from "./message-definitions";

export interface DocumentUploadProps {
  documentType: UploadDocumentType;
  description?: React.ReactNode;
  skipped?: boolean | null;
  onSkipCheckbox?: (checked: boolean) => void;
  skipCheckboxDescription?: string;
  skipCheckboxHelperText?: string;
  header?: string;
  fieldLabel?: string;
  supportedFileExtensions?: string[];
  fileSizeLimitMB?: number;
  slot?: React.ReactNode;
  disabled?: boolean;
  showHelpText?: boolean;
  /** Renders the file size/type caption below the upload control. Default true; client-side
   * validation still runs when false. */
  showConstraints?: boolean;
  error?: string;
  fullScreenDropzone?: boolean;
  onUploadSuccess?: () => void;
  /** "dropzone" (default): the large drag-and-drop area. "compact": a field-style label + small
   * upload button, used when several uploads are stacked on one page. */
  variant?: "dropzone" | "compact";
}

export const DocumentUpload = ({
  header,
  description,
  fieldLabel,
  documentType,
  supportedFileExtensions,
  fileSizeLimitMB,
  skipped,
  onSkipCheckbox,
  skipCheckboxDescription,
  skipCheckboxHelperText,
  slot = null,
  disabled,
  showHelpText = true,
  showConstraints = true,
  error,
  fullScreenDropzone,
  onUploadSuccess,
  variant = "dropzone",
}: DocumentUploadProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const {
    data: { id: applicationId },
  } = useFundingApplication(customerDomain);

  const isValidatedDocumentType =
    documentType === UploadDocumentType.PROFIT_AND_LOSS || documentType === UploadDocumentType.BALANCE_SHEET;

  const baseUrlPath = (isValidatedDocumentType ? VALIDATED_DOCUMENT_TYPE_UPLOADS_API : DOCUMENT_TYPE_UPLOADS_API)
    .replace(":customerSlug", customerDomain)
    .replace(":applicationId", applicationId.toString())
    .replace(":documentType", documentType);

  const createUrlPath = `${baseUrlPath}/upload`;
  const confirmUploadPath = `${baseUrlPath}/confirm`;
  const deleteUploadPath = `${baseUrlPath}/delete`;

  const useDocumentListQuery = isValidatedDocumentType ? useValidatedDocumentQuery : useDocumentQuery;

  const { data, refetch, isLoading } = useDocumentListQuery(customerDomain, applicationId);
  const responseForType = data?.find((resp) => resp.documentType === documentType);
  const files = responseForType?.data ?? [];

  const useUploadManager = uploadManagerHookFactory<FundingApplicationSchemas.File>(
    {
      files,
      createUrlPath,
      confirmUploadPath,
      deleteUploadPath,
      supportedFileExtensions,
      sizeLimitMB: fileSizeLimitMB,
      onUploadSuccess: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
        onUploadSuccess?.();
      },
      onRemoveComplete: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
      },
    },
    intl
  );

  const {
    uploadsState,
    onFilesAdded,
    onFileRemoved,
    sizeLimitMB,
    supportedFileExtensions: supportedExtensions,
    onFilesChanged,
  } = useUploadManager();

  React.useEffect(() => {
    if (!isLoading) {
      onFilesChanged();
    }
  }, [isLoading, onFilesChanged]);

  const hasFiles = Boolean(uploadsState && uploadsState.uploads.length > 0);
  const isSkipped = skipped === true;

  return (
    <Flex direction="column" gap="4">
      {header && <Heading>{header}</Heading>}
      {description}
      {slot}
      {variant === "compact" ? (
        <FileUpload
          label={fieldLabel}
          onFilesAdded={onFilesAdded}
          onFileRemoved={onFileRemoved}
          uploadsState={uploadsState}
          sizeLimitMB={sizeLimitMB}
          acceptedFileTypes={supportedExtensions}
          disabled={disabled || isSkipped}
          error={error}
          multiple
          showConstraints={showConstraints}
        />
      ) : (
        <>
          <UploadArea
            label={fieldLabel}
            onFilesAdded={onFilesAdded}
            sizeLimitMB={sizeLimitMB}
            acceptedFileTypes={supportedExtensions}
            disabled={disabled}
            error={error}
            multiple
            fullScreenDropzone={fullScreenDropzone}
          />
          {uploadsState && uploadsState.uploads.length > 0 ? (
            <FilesList uploadsState={uploadsState} onFileRemoved={onFileRemoved} />
          ) : null}
        </>
      )}
      {onSkipCheckbox ? (
        <Flex direction="column" gap="4">
          {variant !== "compact" && <Divider label={intl.formatMessage(MESSAGES.skipCheckboxDividerLabel)} />}
          <CheckboxField
            name={`skip-document-upload-field-${documentType}`}
            size={variant === "compact" ? "small" : undefined}
            disabled={disabled || (variant === "compact" && hasFiles)}
            options={{
              name: "skip_upload",
              label: skipCheckboxDescription ?? intl.formatMessage(MESSAGES.skipCheckboxLabel),
              helperText: skipCheckboxHelperText,
              // HACK: See https://github.com/wayflyer/wayflyer/pull/21846
              checked: skipped as boolean | undefined,
              onChange: (checked) => {
                onSkipCheckbox(checked);
              },
            }}
          />
        </Flex>
      ) : null}

      {showHelpText && (
        <Text size="xs">
          {intl.formatMessage(MESSAGES.havingTroubleFooterText, {
            link: (str: ReactNode[]) => <Link onClick={handleContactTeam}>{str}</Link>,
          })}
        </Text>
      )}
    </Flex>
  );
};
